import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Bot, Workflow, Code, MessageSquare, Image, Video } from 'lucide-react';

const AICreation = () => {
  const services = [
    {
      icon: Bot,
      title: 'AI Agents',
      description: 'Custom intelligent agents that automate complex tasks, from customer service to data analysis. Built to integrate seamlessly with your existing systems.',
    },
    {
      icon: Workflow,
      title: 'Workflow Automation',
      description: 'End-to-end automation solutions that eliminate manual processes. We design, build, and deploy intelligent workflows that save time and reduce errors.',
    },
    {
      icon: Code,
      title: 'Custom AI Tools',
      description: 'Bespoke AI applications tailored to your specific needs. From internal tools to customer-facing products, we bring your AI vision to life.',
    },
    {
      icon: MessageSquare,
      title: 'Chatbots & Assistants',
      description: 'Conversational AI that enhances customer experience. Intelligent chatbots that understand context, handle complex queries, and learn over time.',
    },
    {
      icon: Image,
      title: 'Generative AI Solutions',
      description: 'Harness the power of generative AI for content creation, design, and innovation. Custom implementations of leading AI models for your use cases.',
    },
    {
      icon: Video,
      title: 'AI-Powered Media',
      description: 'Transform your media production with AI. From automated video editing to AI-generated graphics, we supercharge your creative output.',
    },
  ];

  const projects = [
    {
      title: 'Automated Content Pipeline',
      client: 'Media Company',
      result: '80% faster production',
    },
    {
      title: 'Customer Service Agent',
      client: 'E-commerce Brand',
      result: '60% ticket reduction',
    },
    {
      title: 'Data Analysis Tool',
      client: 'Investment Fund',
      result: '10x faster insights',
    },
  ];

  return (
    <div data-testid="ai-creation-page">
      {/* Hero */}
      <section className="min-h-[70vh] flex items-center relative overflow-hidden">
        <div className="absolute inset-0 bg-elate-black" />
        <motion.div
          className="absolute bottom-0 right-0 w-96 h-96 bg-elate-yellow/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 8, repeat: Infinity }}
        />

        <div className="container-custom relative z-10 pt-32 pb-20">
          <motion.p
            className="caption text-elate-yellow mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            AI Creation
          </motion.p>

          <motion.h1
            className="text-headline text-white mb-8 max-w-4xl"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            We Build
            <br />
            <span className="text-elate-yellow">Intelligent Tools</span>
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl text-gray-400 max-w-2xl mb-10 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Beyond web presence—we're developing in-house AI systems that future-proof brands and grow businesses. From intelligent agents to custom automations, we build what you need to stay ahead.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="creation-cta">
              Discuss Your Project
              <ArrowUpRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding bg-elate-surface">
        <div className="container-custom">
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="caption text-elate-yellow mb-4">What We Build</p>
            <h2 className="text-subhead text-white">
              AI Solutions
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="card-elate p-8 group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <service.icon className="w-10 h-10 text-elate-yellow mb-6 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-semibold text-white mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="caption text-elate-yellow mb-4">Results</p>
            <h2 className="text-subhead text-white">
              Impact We've Made
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <motion.div
                key={project.title}
                className="border border-white/10 p-8 hover:border-elate-yellow/50 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <p className="text-4xl font-heading text-elate-yellow mb-4">
                  {project.result}
                </p>
                <h4 className="text-white font-semibold mb-2">
                  {project.title}
                </h4>
                <p className="text-gray-500 text-sm uppercase tracking-wider">
                  {project.client}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-elate-surface">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-subhead text-white mb-6">
              Have an AI Idea?
            </h2>
            <p className="text-gray-400 mb-8 text-lg">
              Whether it's a simple automation or a complex AI agent, we can help bring it to life. Let's talk about what's possible.
            </p>
            <Link
              to="/contact"
              className="btn-primary inline-flex items-center gap-2"
            >
              Start Building
              <ArrowUpRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AICreation;
